import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { differenceInDays } from 'date-fns';
import { COMPLETE_MENU_STRUCTURE, type MenuNode } from '@/config/menuStructure';

export interface FrequentlyUsedItem {
  id: string;
  path: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }> | string;
  clickCount: number;
  lastAccessed: Date;
  score: number;
}

interface NavigationUsageRow {
  id: string;
  menu_item_id: string;
  menu_path: string;
  menu_label: string | null;
  click_count: number;
  last_accessed_at: string;
}

interface AutoFavoritesSettings {
  enabled: boolean;
  minClicksToShow: number;
  maxItemsToShow: number;
}

// Find icon for menu item by ID
const findMenuItemIcon = (menuItemId: string, nodes: MenuNode[] = COMPLETE_MENU_STRUCTURE): React.ComponentType<{ className?: string }> | string | undefined => {
  for (const node of nodes) {
    if (node.id === menuItemId) {
      return node.icon;
    }
    if (node.children) {
      const found = findMenuItemIcon(menuItemId, node.children);
      if (found) return found;
    }
  }
  return undefined;
};

// Calculate weighted score based on frequency and recency
const calculateScore = (clickCount: number, lastAccessedAt: string): number => {
  const daysSinceAccess = differenceInDays(new Date(), new Date(lastAccessedAt));
  // Exponential decay: items accessed more recently get higher weight
  const recencyFactor = Math.exp(-0.1 * daysSinceAccess);
  return clickCount * recencyFactor;
};

export const useFrequentlyUsedNavigation = () => {
  const { currentBrandId } = useBrandManager();

  // Fetch user settings
  const { data: settings } = useQuery({
    queryKey: ['auto-favorites-settings', currentBrandId],
    queryFn: async (): Promise<AutoFavoritesSettings> => {
      if (!currentBrandId) {
        return { enabled: true, minClicksToShow: 3, maxItemsToShow: 5 };
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { enabled: true, minClicksToShow: 3, maxItemsToShow: 5 };
      }

      const { data, error } = await supabase
        .from('os_navigation_auto_favorites_settings')
        .select('*')
        .eq('user_id', user.id)
        .eq('brand_id', currentBrandId)
        .maybeSingle();

      if (error || !data) {
        return { enabled: true, minClicksToShow: 3, maxItemsToShow: 5 };
      }

      return {
        enabled: data.enabled ?? true,
        minClicksToShow: data.min_clicks_to_show ?? 3,
        maxItemsToShow: data.max_items_to_show ?? 5,
      };
    },
    enabled: !!currentBrandId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch navigation usage data
  const { data: usageData, isLoading, refetch } = useQuery({
    queryKey: ['navigation-usage', currentBrandId],
    queryFn: async (): Promise<NavigationUsageRow[]> => {
      if (!currentBrandId) return [];

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('os_navigation_usage')
        .select('*')
        .eq('user_id', user.id)
        .eq('brand_id', currentBrandId)
        .order('click_count', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching navigation usage:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!currentBrandId && settings?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes (reduced from 1 min to prevent rate limiting)
  });

  // Calculate frequently used items with scoring
  const frequentlyUsedItems = useMemo((): FrequentlyUsedItem[] => {
    if (!usageData || !settings?.enabled) return [];

    const minClicks = settings.minClicksToShow;
    const maxItems = settings.maxItemsToShow;

    const itemsWithScore = usageData
      .filter(item => item.click_count >= minClicks)
      .map(item => ({
        id: item.menu_item_id,
        path: item.menu_path,
        label: item.menu_label || item.menu_item_id,
        icon: findMenuItemIcon(item.menu_item_id),
        clickCount: item.click_count,
        lastAccessed: new Date(item.last_accessed_at),
        score: calculateScore(item.click_count, item.last_accessed_at),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, maxItems);

    return itemsWithScore;
  }, [usageData, settings]);

  // Update settings
  const updateSettings = async (newSettings: Partial<AutoFavoritesSettings>) => {
    if (!currentBrandId) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('os_navigation_auto_favorites_settings')
      .upsert({
        user_id: user.id,
        brand_id: currentBrandId,
        enabled: newSettings.enabled ?? settings?.enabled ?? true,
        min_clicks_to_show: newSettings.minClicksToShow ?? settings?.minClicksToShow ?? 3,
        max_items_to_show: newSettings.maxItemsToShow ?? settings?.maxItemsToShow ?? 5,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,brand_id',
      });

    if (error) {
      console.error('Error updating auto-favorites settings:', error);
    }
  };

  // Reset usage data
  const resetUsageData = async () => {
    if (!currentBrandId) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('os_navigation_usage')
      .delete()
      .eq('user_id', user.id)
      .eq('brand_id', currentBrandId);

    if (error) {
      console.error('Error resetting usage data:', error);
    } else {
      refetch();
    }
  };

  return {
    frequentlyUsedItems,
    isLoading,
    settings: settings ?? { enabled: true, minClicksToShow: 3, maxItemsToShow: 5 },
    updateSettings,
    resetUsageData,
    refetch,
  };
};
